import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";
import { ReactNode, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetBenefitComparisonSummaryQuery } from "store/queries/benefitComparison";
import {
  ComparisonBenefit,
  ComparisonEngine,
  ComparisonIncompleteReason,
  ComparisonPattern,
  IComparisonCell,
  IComparisonChecksRequest,
  IComparisonCluster,
  IComparisonFilters,
} from "types/BenefitComparison/BenefitComparison";

import { BenefitComparisonChecks } from "./BenefitComparisonChecks";
import { BenefitComparisonCluster } from "./BenefitComparisonCluster";
import {
  aiRemitAccuracyText,
  CHECK_DATE_OPTIONS,
  checkDateRange,
  CheckDatePreset,
  COMPARISON_BENEFIT_LABELS,
  COMPARISON_BENEFITS,
  COMPARISON_ENGINE_LABELS,
  COMPARISON_ENGINE_SENTENCES,
  comparedChecks,
  engineToggleVisible,
  formatComparisonValue,
  INCOMPLETE_REASON_LABELS,
  LITTLE_DATA_CHECKS,
  PATTERN_LABELS,
  PATTERN_ORDER,
} from "./benefitComparisonWording";
import { BenefitReviewQueue } from "./BenefitReviewQueue";
import { BenefitVerifiedStatus } from "./BenefitVerifiedStatus";
import { READ_FROM_LABELS, relativeTime } from "./remitAccuracyWording";

const PARAM = {
  account: "account",
  payer: "payer",
  benefit: "benefit",
  days: "days",
  engine: "engine",
  clusterAccount: "clusterAccount",
  clusterPayer: "clusterPayer",
  clusterPlan: "clusterPlan",
  clusterNoPlan: "clusterNoPlan",
  clusterBenefit: "clusterBenefit",
  clusterReadFrom: "clusterReadFrom",
  view: "view",
} as const;

const CLUSTER_PARAMS = [
  PARAM.clusterAccount,
  PARAM.clusterPayer,
  PARAM.clusterPlan,
  PARAM.clusterNoPlan,
  PARAM.clusterBenefit,
  PARAM.clusterReadFrom,
];

const PATTERN_FIELD: Record<ComparisonPattern, keyof IComparisonCell> = {
  ALL_AGREE: "allAgree",
  ONLY_ENGINE_DIFFERS: "onlyEngineDiffers",
  ONLY_AI_DIFFERS: "onlyAiDiffers",
  ONLY_LEGACY_DIFFERS: "onlyLegacyDiffers",
  ALL_DIFFER: "allDiffer",
};

const oneOf = <T extends string>(value: string | null, allowed: T[]) =>
  value !== null && allowed.includes(value as T) ? (value as T) : undefined;

interface ISelection {
  benefit: ComparisonBenefit;
  pattern?: ComparisonPattern;
  incompleteReason?: ComparisonIncompleteReason;
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-color-secondary">{children}</span>;
}

function Count({
  count,
  label,
  onOpen,
  testId,
  note,
}: {
  count: number;
  label: string;
  onOpen: () => void;
  testId: string;
  note?: string;
}) {
  return (
    <div className="flex align-items-baseline gap-2">
      <Button
        type="button"
        className="p-button-link p-0 font-semibold"
        label={count.toLocaleString()}
        disabled={count === 0}
        onClick={onOpen}
        data-testid={testId}
      />
      <Muted>
        {label}
        {note ? ` · ${note}` : ""}
      </Muted>
    </div>
  );
}

/** One benefit: the six-way split, every count opening its checks. */
function Tile({
  cell,
  onOpen,
}: {
  cell: IComparisonCell;
  onOpen: (selection: ISelection) => void;
}) {
  const { benefit } = cell;
  const reasons = (
    Object.entries(cell.incompleteReasons) as [
      ComparisonIncompleteReason,
      number
    ][]
  ).filter(([, count]) => count > 0);
  return (
    <div
      className="border-1 surface-border border-round p-3 h-full flex flex-column gap-1"
      data-testid={`benefitComparison.tile.${benefit}`}
    >
      <span className="font-semibold text-color-secondary">
        {COMPARISON_BENEFIT_LABELS[benefit]}
      </span>
      <span className="text-2xl font-semibold mb-1">
        {cell.checks.toLocaleString()} checks
      </span>
      {PATTERN_ORDER.map((pattern) => (
        <Count
          key={pattern}
          count={cell[PATTERN_FIELD[pattern]] as number}
          label={PATTERN_LABELS[pattern]}
          note={
            pattern === "ALL_AGREE" && cell.zeroVsAbsent > 0
              ? `${cell.zeroVsAbsent} rely on $0 = no value`
              : undefined
          }
          onOpen={() => onOpen({ benefit, pattern })}
          testId={`benefitComparison.tile.${benefit}.${pattern}`}
        />
      ))}
      <div className="border-top-1 surface-border pt-1 mt-1">
        <Muted>{cell.incomplete.toLocaleString()} incomplete</Muted>
        {reasons.map(([reason, count]) => (
          <Count
            key={reason}
            count={count}
            label={INCOMPLETE_REASON_LABELS[reason]}
            onOpen={() => onOpen({ benefit, incompleteReason: reason })}
            testId={`benefitComparison.tile.${benefit}.${reason}`}
          />
        ))}
      </div>
    </div>
  );
}

function Range({
  min,
  max,
  hadNoValue,
  benefit,
}: {
  min: number | null;
  max: number | null;
  hadNoValue: boolean;
  benefit: ComparisonBenefit;
}) {
  let range = "";
  if (min !== null && max !== null)
    range =
      min === max
        ? formatComparisonValue(min, benefit)
        : `${formatComparisonValue(min, benefit)} – ${formatComparisonValue(
            max,
            benefit
          )}`;
  return (
    <span className="white-space-nowrap">
      {range}
      {range && hadNoValue ? " / " : ""}
      {hadNoValue && <Muted>no value</Muted>}
    </span>
  );
}

const clusterNameBody = (row: IComparisonCluster) => (
  <div className="flex flex-column gap-1">
    <span className="font-semibold">
      {row.accountName} · {row.payerName}
    </span>
    <Muted>{row.planName ?? "No plan name"}</Muted>
  </div>
);

const legacyRangeBody = ({
  benefit,
  legacyMin,
  legacyMax,
  legacyHadNoValue,
}: IComparisonCluster) => (
  <Range
    min={legacyMin}
    max={legacyMax}
    hadNoValue={legacyHadNoValue}
    benefit={benefit}
  />
);
const engineRangeBody = ({
  benefit,
  engineMin,
  engineMax,
  engineHadNoValue,
}: IComparisonCluster) => (
  <Range
    min={engineMin}
    max={engineMax}
    hadNoValue={engineHadNoValue}
    benefit={benefit}
  />
);
const aiRangeBody = ({
  benefit,
  aiMin,
  aiMax,
  aiHadNoValue,
}: IComparisonCluster) => (
  <Range min={aiMin} max={aiMax} hadNoValue={aiHadNoValue} benefit={benefit} />
);

const aiAccuracyBody = (row: IComparisonCluster) => (
  <Muted>{aiRemitAccuracyText(row.aiRemitAccuracy)}</Muted>
);

/**
 * CLE-2820 PR 3 — LEGACY VS ENGINE VS AI: on every live check, remit or not, where the three pickers agree and where
 * they don't. Internal, SuperAdmin only. A disagreement is a lead to investigate, not a miss; the Remit accuracy tab
 * is the one that says which side is right.
 */
export function BenefitComparisonTab() {
  const [params, setParams] = useSearchParams();
  const [selection, setSelection] = useState<ISelection | null>(null);

  const accountId = params.get(PARAM.account) ?? undefined;
  const payerId = params.get(PARAM.payer) ?? undefined;
  const benefit = oneOf(params.get(PARAM.benefit), COMPARISON_BENEFITS);
  const days: CheckDatePreset =
    oneOf(
      params.get(PARAM.days),
      CHECK_DATE_OPTIONS.map((option) => option.value)
    ) ?? "30";
  /**
   * CLE-2820 — today's config is the DEFAULT side: `QUOTED` picks only exist for checks made after the live writer
   * shipped, so at-time-of-check is mostly "no engine pick" for now. `?engine=check` still asks for it explicitly and
   * is honoured whether or not the toggle is on screen; `?engine=today` (what the toggle used to write) still resolves
   * to today's config, which is now also what no parameter means.
   */
  const engine: ComparisonEngine =
    params.get(PARAM.engine) === "check" ? "QUOTED" : "REPLAY";
  /**
   * CLE-2820 PR 4 — the review queue is a VIEW of the same tab, on the same filters, not a new tab.
   * CLE-2821 adds a third: verified checks scored against today's engine. It ignores the date window
   * (the truth set is all-time) but keeps account, payer and benefit.
   */
  const viewParam = params.get(PARAM.view);
  const view =
    viewParam === "review" || viewParam === "verified" ? viewParam : "compare";

  const update = (changes: Record<string, string | undefined>, push = false) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        Object.entries(changes).forEach(([key, value]) => {
          if (value === undefined) next.delete(key);
          else next.set(key, value);
        });
        return next;
      },
      { replace: !push }
    );

  const filters: IComparisonFilters = useMemo(
    () => ({ accountId, payerId, benefit, engine, ...checkDateRange(days) }),
    [accountId, payerId, benefit, engine, days]
  );
  const { data, isLoading, isError } =
    useGetBenefitComparisonSummaryQuery(filters);
  const summary = data?.data;
  // The at-time-of-check side's own counts, ONLY to decide whether offering it is worth it. On that side it is the
  // same cache entry as the query above, so it costs a request only while the toggle is hidden.
  const quoted = useGetBenefitComparisonSummaryQuery({
    ...filters,
    engine: "QUOTED",
  });
  const showEngineToggle = engineToggleVisible(quoted.data?.data.headline);

  const clusterAccount = params.get(PARAM.clusterAccount);
  const clusterBenefit = oneOf(
    params.get(PARAM.clusterBenefit),
    COMPARISON_BENEFITS
  );
  const clusterReadFrom = params.get(PARAM.clusterReadFrom);
  if (clusterAccount && clusterBenefit && clusterReadFrom) {
    const request: IComparisonChecksRequest = {
      ...filters,
      accountId: clusterAccount,
      payerId: params.get(PARAM.clusterPayer) ?? "none",
      benefit: clusterBenefit,
      readFrom: clusterReadFrom,
      ...(params.get(PARAM.clusterNoPlan) === "1"
        ? { planNameMissing: true }
        : { planName: params.get(PARAM.clusterPlan) ?? undefined }),
    };
    return (
      <Surface data-testid="benefitComparisonTab">
        <BenefitComparisonCluster
          request={request}
          onBack={() =>
            update(
              Object.fromEntries(CLUSTER_PARAMS.map((key) => [key, undefined])),
              true
            )
          }
        />
      </Surface>
    );
  }

  const cells = (summary?.headline ?? []).filter(
    (cell) => !benefit || cell.benefit === benefit
  );
  const littleData =
    summary &&
    (!summary.aiPicksSince || comparedChecks(cells) < LITTLE_DATA_CHECKS);

  const openCluster = (row: IComparisonCluster) =>
    update(
      {
        [PARAM.clusterAccount]: row.accountId,
        [PARAM.clusterPayer]:
          row.payerId === null ? "none" : String(row.payerId),
        [PARAM.clusterPlan]: row.planName ?? undefined,
        [PARAM.clusterNoPlan]: row.planName === null ? "1" : undefined,
        [PARAM.clusterBenefit]: row.benefit,
        [PARAM.clusterReadFrom]: row.readFrom,
      },
      true
    );

  const openClusterBody = (row: IComparisonCluster) => (
    <Button
      type="button"
      label="Analyze"
      className="p-button-text p-button-sm"
      data-testid="benefitComparison.cluster.open"
      onClick={() => openCluster(row)}
    />
  );

  return (
    <Surface data-testid="benefitComparisonTab">
      <div className="flex flex-wrap justify-content-between gap-2 mb-4">
        <Muted>
          Where legacy, the benefit engine and the AI pick different values for
          the same eligibility check, with or without a remit. A disagreement is
          a lead to investigate, not a miss — Remit accuracy says which side is
          right.
        </Muted>
        {engine === "REPLAY" && (
          <Muted>
            {summary?.lastReplayAt
              ? `Today's config re-run ${relativeTime(summary.lastReplayAt)}`
              : "Today's config has not been re-run yet"}
          </Muted>
        )}
      </div>

      <div className="flex flex-wrap align-items-end gap-3 mb-4">
        <div className="flex flex-column gap-1">
          <label htmlFor="benefitComparison.account">Account</label>
          <Dropdown
            inputId="benefitComparison.account"
            value={accountId ?? null}
            options={summary?.options.accounts ?? []}
            optionLabel="accountName"
            optionValue="accountId"
            placeholder="All accounts"
            showClear
            filter
            className="w-16rem"
            onChange={(e) => update({ [PARAM.account]: e.value ?? undefined })}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="benefitComparison.payer">Payer</label>
          <Dropdown
            inputId="benefitComparison.payer"
            value={payerId ?? null}
            options={(summary?.options.payers ?? []).map((payer) => ({
              label: payer.payerName,
              value: String(payer.payerId),
            }))}
            placeholder="All payers"
            showClear
            filter
            className="w-14rem"
            onChange={(e) => update({ [PARAM.payer]: e.value ?? undefined })}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="benefitComparison.benefit">Benefit</label>
          <Dropdown
            inputId="benefitComparison.benefit"
            value={benefit ?? null}
            options={COMPARISON_BENEFITS.map((each) => ({
              label: COMPARISON_BENEFIT_LABELS[each],
              value: each,
            }))}
            placeholder="All benefits"
            showClear
            className="w-14rem"
            onChange={(e) => update({ [PARAM.benefit]: e.value ?? undefined })}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="benefitComparison.days">Check date</label>
          <Dropdown
            inputId="benefitComparison.days"
            value={days}
            options={CHECK_DATE_OPTIONS}
            className="w-12rem"
            onChange={(e) =>
              update({ [PARAM.days]: e.value === "30" ? undefined : e.value })
            }
          />
        </div>
        <div className="flex flex-column gap-1 ml-auto">
          <span>View</span>
          <SelectButton
            value={view}
            options={[
              { value: "compare", label: "Compare" },
              { value: "review", label: "Review queue" },
              { value: "verified", label: "Verified" },
            ]}
            onChange={(e) =>
              e.value &&
              update({
                [PARAM.view]: e.value === "compare" ? undefined : e.value,
              })
            }
            data-testid="benefitComparison.view"
          />
        </div>
        {showEngineToggle && (
          <div className="flex flex-column gap-1">
            <span>Engine side</span>
            <SelectButton
              value={engine}
              options={(["QUOTED", "REPLAY"] as ComparisonEngine[]).map(
                (value) => ({ value, label: COMPARISON_ENGINE_LABELS[value] })
              )}
              onChange={(e) =>
                e.value &&
                update({
                  [PARAM.engine]: e.value === "QUOTED" ? "check" : undefined,
                })
              }
              data-testid="benefitComparison.engine"
            />
          </div>
        )}
      </div>

      {isError && (
        <Message
          severity="error"
          text="Legacy vs Engine vs AI could not be loaded."
        />
      )}
      {view === "verified" && <BenefitVerifiedStatus filters={filters} />}
      {view === "review" && (
        <BenefitReviewQueue filters={filters} onOpenCluster={openCluster} />
      )}
      {view === "compare" && (
        <>
          {summary && (
            <div
              className="flex flex-wrap gap-4 mb-3"
              data-testid="benefitComparison.verdicts"
            >
              <Muted>
                {summary.verdicts.recorded.toLocaleString()} of{" "}
                {summary.verdicts.disagreements.toLocaleString()} disagreements
                reviewed
                {summary.verdicts.cannotDetermine > 0
                  ? ` · ${summary.verdicts.cannotDetermine} can’t tell`
                  : ""}
              </Muted>
              {/* 🚨 A remit OUTRANKS a verdict. Counted here; both figures stay on the check. */}
              {summary.verdicts.remitConflicts > 0 && (
                <Muted>
                  ⚠️ {summary.verdicts.remitConflicts.toLocaleString()} verdicts
                  a remit disagrees with
                </Muted>
              )}
            </div>
          )}
          {littleData && (
            <Message
              severity="info"
              className="mb-3"
              data-testid="benefitComparison.littleData"
              text={`AI picks started 2026-09-17, so there is little to compare yet (${comparedChecks(
                cells
              ).toLocaleString()} checks with all three sides). Counts grow as live checks arrive; older checks need the backfill.`}
            />
          )}

          {!showEngineToggle && (
            <p
              className="mt-0 mb-3 text-color-secondary"
              data-testid="benefitComparison.engineSide"
            >
              {COMPARISON_ENGINE_SENTENCES[engine]}
            </p>
          )}

          <div className="grid">
            {cells.map((cell) => (
              <div key={cell.benefit} className="col-12 md:col-6 xl:col">
                <Tile cell={cell} onOpen={setSelection} />
              </div>
            ))}
          </div>

          <div className="flex flex-column gap-2 mt-5 mb-3">
            <h3 className="m-0">Where they disagree</h3>
            <Muted>
              Checks grouped by account, payer, plan, benefit and where the
              engine read from. Ranked by checks where the engine is the odd one
              out. The AI&apos;s accuracy against remits is context, not a
              verdict on the cluster.
            </Muted>
          </div>
          <DataTable
            value={summary?.clusters ?? []}
            loading={isLoading}
            emptyMessage="No disagreements."
            data-testid="benefitComparison.clusters"
          >
            <Column
              header="#"
              body={(_row, options) => options.rowIndex + 1}
              style={{ width: "3rem" }}
            />
            <Column header="Account · Payer · Plan" body={clusterNameBody} />
            <Column
              header="Benefit"
              body={(row: IComparisonCluster) =>
                COMPARISON_BENEFIT_LABELS[row.benefit]
              }
            />
            <Column
              header="Disagree"
              body={(row: IComparisonCluster) =>
                `${row.disagreements} of ${row.disagreements + row.allAgree}`
              }
            />
            <Column
              header="Only engine differs"
              body={(row: IComparisonCluster) => row.onlyEngineDiffers}
            />
            <Column header="Legacy" body={legacyRangeBody} />
            <Column header="Engine" body={engineRangeBody} />
            <Column header="AI" body={aiRangeBody} />
            <Column
              header="Engine read from"
              body={(row: IComparisonCluster) =>
                READ_FROM_LABELS[row.readFrom] ?? row.readFrom
              }
            />
            <Column header="AI vs remits" body={aiAccuracyBody} />
            <Column header="" body={openClusterBody} />
          </DataTable>
        </>
      )}

      <Dialog
        header={
          selection
            ? `${COMPARISON_BENEFIT_LABELS[selection.benefit]} · ${
                selection.pattern
                  ? PATTERN_LABELS[selection.pattern]
                  : INCOMPLETE_REASON_LABELS[
                      selection.incompleteReason as ComparisonIncompleteReason
                    ]
              }`
            : ""
        }
        visible={Boolean(selection)}
        style={{ width: "min(1200px, 95vw)" }}
        onHide={() => setSelection(null)}
      >
        {selection && (
          <Surface>
            <BenefitComparisonChecks
              request={{ ...filters, ...selection }}
              showAccount
            />
          </Surface>
        )}
      </Dialog>
    </Surface>
  );
}
